import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { ChevronRight, FolderOpen, Plus, X } from 'lucide-react';
import { DOCUMENTS_QUERY, ME_QUERY, SYNC_STATUS_QUERY } from '../graphql/queries';
import { SAVE_DOCUMENT_TEXT_MUTATION } from '../graphql/mutations';
import { FetchErrorCard } from '../components/FetchErrorCard';
import { DocRowMeta } from '../components/DocRowMeta';
import { OutboxSummaryBanner } from '../components/OutboxBanner';
import { DocTreeNav } from '../components/DocTreeNav';
import { docTypeIcon } from '../lib/docIcons';
import { FOCUS_RING_CLASSES, trapTabKey } from '../lib/focusTrap';
import { useScrollLock } from '../hooks/useScrollLock';
import { fileStem, journalDayFromPath, typeFromPath } from '../lib/docView';

interface DocumentSummary {
  path: string;
  title: string;
  type: string | null;
  updatedAt: string;
  treeLabel: string;
  spaceId: string | null;
}

interface DocumentsData {
  documents: DocumentSummary[];
}

interface SpaceSummary {
  id: string;
  name: string;
  slug: string;
}

interface MeData {
  me: { spaces: SpaceSummary[] } | null;
}

/** "today", "yesterday" or "Jul 7, 2026" in the local tz. */
function formatUpdated(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return 'today';
  if (d.toDateString() === y.toDateString()) return 'yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Display type for an index row: front-matter `type` else folder convention. */
function docDisplayType(doc: DocumentSummary): string | null {
  return doc.type ?? typeFromPath(doc.path);
}

/** Row title: the filename stem — the index reads exactly like the file tree. */
function docDisplayTitle(doc: DocumentSummary): string {
  return fileStem(doc.path);
}

/** Path within its own tree: space docs drop the leading `<slug>/` prefix. */
function relativePath(doc: DocumentSummary): string {
  if (doc.spaceId) {
    const idx = doc.path.indexOf('/');
    return idx >= 0 ? doc.path.slice(idx + 1) : doc.path;
  }
  return doc.path;
}

/** The folder portion of a relative path ("" for a root-level document). */
function folderOf(relPath: string): string {
  const idx = relPath.lastIndexOf('/');
  return idx >= 0 ? relPath.slice(0, idx) : '';
}

interface TreeGroup {
  label: string;
  folders: { folder: string; docs: DocumentSummary[] }[];
}

/** Groups documents by tree (Personal / each space), then by folder within the tree. */
function groupDocuments(docs: DocumentSummary[]): TreeGroup[] {
  const trees = new Map<string, Map<string, DocumentSummary[]>>();
  for (const doc of docs) {
    if (!trees.has(doc.treeLabel)) trees.set(doc.treeLabel, new Map());
    const folders = trees.get(doc.treeLabel)!;
    const folder = folderOf(relativePath(doc));
    if (!folders.has(folder)) folders.set(folder, []);
    folders.get(folder)!.push(doc);
  }

  const result: TreeGroup[] = [];
  for (const [label, folders] of trees) {
    const folderList = [...folders.entries()]
      .map(([folder, list]) => ({
        folder,
        docs: [...list].sort((a, b) => {
          // Journal days read newest-first; everything else sorts by
          // filename, matching what the rows now display.
          const dayA = journalDayFromPath(a.path);
          const dayB = journalDayFromPath(b.path);
          if (dayA && dayB) return dayB.localeCompare(dayA);
          return fileStem(a.path).localeCompare(fileStem(b.path));
        }),
      }))
      .sort((a, b) => a.folder.localeCompare(b.folder));
    result.push({ label, folders: folderList });
  }
  // Personal first, then spaces alphabetically.
  result.sort((a, b) => {
    if (a.label === 'Personal') return -1;
    if (b.label === 'Personal') return 1;
    return a.label.localeCompare(b.label);
  });
  return result;
}

/** Documents page: the caller's whole document tree (personal + shared spaces). */
export function DocumentsPage() {
  const { data, loading, error } = useQuery<DocumentsData>(DOCUMENTS_QUERY);
  // Sync trouble surfaces as ONE calm line here linking to Profile — never
  // per-document noise (GIT_SYNC_PLAN decision 2). Errors are ignored: an
  // unavailable status must never degrade the documents page.
  const { data: syncData } = useQuery<{ syncStatus: { state: string | null } }>(SYNC_STATUS_QUERY, {
    errorPolicy: 'ignore',
  });
  const [showNew, setShowNew] = useState(false);
  const newButtonRef = useRef<HTMLButtonElement>(null);

  const groups = useMemo(() => groupDocuments(data?.documents ?? []), [data?.documents]);
  const syncNeedsAttention = syncData?.syncStatus?.state === 'attention';

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4 px-1 pt-4 sm:pt-6 pb-2">
        <h1 className="text-2xl font-display font-semibold text-[var(--text-primary)]">
          Documents
        </h1>
        <div className="flex items-center gap-2">
          <DocTreeNav />
          <button
            ref={newButtonRef}
            type="button"
            onClick={() => setShowNew(true)}
            className="btn btn-primary text-sm gap-2"
          >
            <Plus className="w-4 h-4" aria-hidden /> New document
          </button>
        </div>
      </div>

      <OutboxSummaryBanner />

      {syncNeedsAttention && (
        <p
          role="status"
          className="glass-panel rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800"
        >
          Sync needs attention —{' '}
          <Link to="/profile" className={`underline font-medium ${FOCUS_RING_CLASSES}`}>
            review it in Profile
          </Link>
          . Your documents are safe either way.
        </p>
      )}

      {error && !data ? (
        <FetchErrorCard>
          Something went wrong loading your documents. Try again in a moment.
        </FetchErrorCard>
      ) : loading && !data ? (
        <div className="space-y-2.5" aria-hidden="true">
          <span className="skeleton block h-5 w-1/3 rounded-md" />
          <span className="skeleton block h-14 w-full rounded-xl" />
          <span className="skeleton block h-14 w-full rounded-xl" />
        </div>
      ) : groups.length === 0 ? (
        <section className="glass-panel rounded-2xl p-8 border border-[var(--card-border)] shadow-soft text-center">
          <p className="text-[var(--text-secondary)]">
            No documents yet. Tell me something to capture, or start one yourself.
          </p>
        </section>
      ) : (
        groups.map((group) => (
          <section
            key={group.label}
            aria-label={group.label}
            className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft animate-fade-in-up"
          >
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4">{group.label}</h2>
            <div className="space-y-4">
              {group.folders.map(({ folder, docs }) => (
                <div key={folder || '(root)'}>
                  {folder && (
                    <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-[var(--text-tertiary)] mb-1.5">
                      <FolderOpen className="w-3.5 h-3.5" aria-hidden />
                      {folder}
                    </p>
                  )}
                  {folder === 'journal' ? (
                    <JournalFolderList docs={docs} />
                  ) : (
                    <ul className="space-y-1">
                      {docs.map((doc) => (
                        <DocRow key={doc.path} doc={doc} />
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))
      )}

      {showNew && <NewDocumentDialog onClose={() => setShowNew(false)} />}
    </div>
  );
}

/** One index row: type icon tile, title, per-type meta, updated-at. */
function DocRow({ doc }: { doc: DocumentSummary }) {
  const updated = formatUpdated(doc.updatedAt);
  const type = docDisplayType(doc);
  const Icon = docTypeIcon(type);
  return (
    <li>
      <Link
        to={`/docs/${doc.path}`}
        className={`flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-[color-mix(in_oklab,var(--text-primary)_5%,transparent)] transition-colors ${FOCUS_RING_CLASSES}`}
      >
        <span
          className="w-8 h-8 rounded-xl bg-[color-mix(in_oklab,var(--marine-400)_12%,transparent)] text-[var(--accent-ink)] flex items-center justify-center shrink-0"
          aria-hidden
        >
          <Icon className="w-4 h-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium text-[var(--text-primary)]">
            {docDisplayTitle(doc)}
          </span>
          {(type === 'list' || type === 'note') && <DocRowMeta path={doc.path} type={type} />}
        </span>
        {updated && <span className="text-xs text-[var(--text-tertiary)] shrink-0">{updated}</span>}
      </Link>
    </li>
  );
}

/** The last 7 local calendar days (today included), as `YYYY-MM-DD` strings. */
function lastSevenDays(): Set<string> {
  const days = new Set<string>();
  const pad = (n: number) => String(n).padStart(2, '0');
  for (let i = 0; i < 7; i += 1) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.add(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
  }
  return days;
}

/** "July 2026" for a `YYYY-MM` key, in the local locale. */
function monthLabel(ym: string): string {
  const [year, month] = ym.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
}

/**
 * The journal folder's rows, grouped so years of days don't drown the index:
 * the current week's days render expanded up top, everything older folds into
 * per-month collapsible sections ("July 2026"), newest month first, all
 * collapsed until tapped. Non-day journal documents stay plain rows.
 */
function JournalFolderList({ docs }: { docs: DocumentSummary[] }) {
  const [openMonths, setOpenMonths] = useState<Set<string>>(new Set());

  const { thisWeek, months, others } = useMemo(() => {
    const week = lastSevenDays();
    const thisWeek: DocumentSummary[] = [];
    const others: DocumentSummary[] = [];
    const byMonth = new Map<string, DocumentSummary[]>();
    for (const doc of docs) {
      const day = journalDayFromPath(doc.path);
      if (!day) {
        others.push(doc);
      } else if (week.has(day)) {
        thisWeek.push(doc);
      } else {
        const ym = day.slice(0, 7);
        if (!byMonth.has(ym)) byMonth.set(ym, []);
        byMonth.get(ym)!.push(doc);
      }
    }
    // Docs arrive newest-first (groupDocuments sorts journal days desc), so
    // insertion order already yields months newest-first.
    const months = [...byMonth.entries()].map(([ym, list]) => ({ ym, docs: list }));
    return { thisWeek, months, others };
  }, [docs]);

  const toggleMonth = (ym: string) => {
    setOpenMonths((prev) => {
      const next = new Set(prev);
      if (next.has(ym)) next.delete(ym);
      else next.add(ym);
      return next;
    });
  };

  return (
    <div className="space-y-2">
      {others.length > 0 && (
        <ul className="space-y-1">
          {others.map((doc) => (
            <DocRow key={doc.path} doc={doc} />
          ))}
        </ul>
      )}

      {thisWeek.length > 0 && (
        <div>
          {months.length > 0 && (
            <p className="px-3 text-xs font-medium text-[var(--text-tertiary)] mb-1">This week</p>
          )}
          <ul className="space-y-1">
            {thisWeek.map((doc) => (
              <DocRow key={doc.path} doc={doc} />
            ))}
          </ul>
        </div>
      )}

      {months.map(({ ym, docs: monthDocs }) => {
        const open = openMonths.has(ym);
        return (
          <div key={ym}>
            <button
              type="button"
              onClick={() => toggleMonth(ym)}
              aria-expanded={open}
              className={`w-full min-h-11 flex items-center gap-2 rounded-xl px-3 py-2 text-left hover:bg-[color-mix(in_oklab,var(--text-primary)_5%,transparent)] transition-colors ${FOCUS_RING_CLASSES}`}
            >
              <ChevronRight
                className={`w-4 h-4 text-[var(--text-tertiary)] shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
                aria-hidden
              />
              <span className="min-w-0 flex-1 truncate font-medium text-[var(--text-primary)]">
                {monthLabel(ym)}
              </span>
              <span className="text-xs text-[var(--text-tertiary)] shrink-0">
                {monthDocs.length} {monthDocs.length === 1 ? 'day' : 'days'}
              </span>
            </button>
            {open && (
              <ul className="space-y-1 mt-1">
                {monthDocs.map((doc) => (
                  <DocRow key={doc.path} doc={doc} />
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface NewDocumentDialogProps {
  onClose: () => void;
}

type NewDocError = 'empty_path' | 'save' | null;

/** Creates a new document at a chosen path (optionally inside a shared space). */
function NewDocumentDialog({ onClose }: NewDocumentDialogProps) {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const [spaceId, setSpaceId] = useState('');
  const [path, setPath] = useState('');
  const [content, setContent] = useState('');
  const [errorKind, setErrorKind] = useState<NewDocError>(null);

  useScrollLock(true);

  const { data: meData } = useQuery<MeData>(ME_QUERY);
  const spaces = meData?.me?.spaces ?? [];

  const [saveText, { loading: saving }] = useMutation(SAVE_DOCUMENT_TEXT_MUTATION, {
    refetchQueries: ['Documents'],
    awaitRefetchQueries: true,
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    trapTabKey(e, panelRef.current);
  };

  const fullPath = () => {
    const trimmed = path.trim().replace(/^\/+/, '');
    if (!spaceId) return trimmed;
    const slug = spaces.find((s) => s.id === spaceId)?.slug;
    return slug ? `${slug}/${trimmed}` : trimmed;
  };

  const handleCreate = async () => {
    const trimmed = path.trim();
    if (!trimmed) {
      setErrorKind('empty_path');
      return;
    }
    setErrorKind(null);
    const target = fullPath();
    try {
      await saveText({ variables: { path: target, content } });
      navigate(`/docs/${target}`);
    } catch {
      setErrorKind('save');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        className="glass-panel bg-[var(--surface)] w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 shadow-warm animate-fade-in-up max-h-[min(90dvh,100%)] overflow-y-auto overscroll-contain safe-bottom"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-document-title"
        tabIndex={-1}
      >
        <div
          aria-hidden
          className="mx-auto mb-4 h-1.5 w-10 rounded-full bg-[var(--border)] sm:hidden"
        />
        <div className="flex items-center justify-between mb-4">
          <h2 id="new-document-title" className="text-xl font-semibold text-[var(--text-primary)]">
            New document
          </h2>
          <button
            type="button"
            onClick={onClose}
            className={`p-2 hover:bg-[color-mix(in_oklab,var(--text-primary)_7%,transparent)] rounded-full transition-colors ${FOCUS_RING_CLASSES}`}
            aria-label="Close"
          >
            <X className="w-5 h-5 text-[var(--text-tertiary)]" aria-hidden />
          </button>
        </div>

        <div className="space-y-4">
          {spaces.length > 0 && (
            <div>
              <label htmlFor="new-doc-space" className="label">
                Where
              </label>
              <select
                id="new-doc-space"
                value={spaceId}
                onChange={(e) => setSpaceId(e.target.value)}
                className="mt-1"
              >
                <option value="">Personal</option>
                {spaces.map((space) => (
                  <option key={space.id} value={space.id}>
                    {space.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label htmlFor="new-doc-path" className="label">
              Path
            </label>
            <input
              id="new-doc-path"
              type="text"
              value={path}
              onChange={(e) => {
                setPath(e.target.value);
                if (errorKind === 'empty_path') setErrorKind(null);
              }}
              placeholder="notes/ideas.md"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              aria-invalid={errorKind === 'empty_path' ? true : undefined}
              className="mt-1 font-mono text-sm"
            />
            <p className="help mt-1">A path like {`notes/ideas.md`}. Markdown is welcome.</p>
          </div>

          <div>
            <label htmlFor="new-doc-content" className="label">
              Content
            </label>
            <textarea
              id="new-doc-content"
              rows={6}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="# Ideas"
              spellCheck={false}
              className="mt-1 font-mono text-sm"
            />
          </div>

          {errorKind === 'empty_path' && (
            <p role="alert" className="text-sm text-[var(--text-primary)]">
              Give the document a path first.
            </p>
          )}
          {errorKind === 'save' && (
            <div
              role="alert"
              className="bg-[color-mix(in_oklab,var(--coral-400)_10%,transparent)] border border-[color-mix(in_oklab,var(--coral-400)_35%,transparent)] text-[var(--text-primary)] px-4 py-3 rounded-xl text-sm"
            >
              This wasn't created. Something went wrong on our end — try again.
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button type="button" onClick={onClose} className="btn btn-ghost" disabled={saving}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreate}
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? 'Creating…' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
